import { useMediaQuery, useTheme } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { MediaPermissionPairDto } from '@/types/dtos/media/media-settings.dto';
import { usePatchFlowMediaSettingsMutation } from '@/state/mutations/flows/use-patch-flow-media-settings';
import { usePatchSessionMediaSettingsMutation } from '@/state/mutations/sessions/use-patch-session-media-settings';
import type { ContributorFlowPlaylistScope } from '@/types/dtos/media/media-settings.dto';
import { PlaylistMediaPermissionMenu } from '@/components/media/playlist-media-permission-menu';

type FlowScopeProps = {
  mode: 'flowScope';
  flowId: string;
  scope: ContributorFlowPlaylistScope;
  stored: MediaPermissionPairDto;
  effective: MediaPermissionPairDto;
  canEdit: boolean;
};

type SessionProps = {
  mode: 'session';
  sessionId: string;
  stored: MediaPermissionPairDto;
  effective: MediaPermissionPairDto;
  canEdit: boolean;
  disabledTooltip?: string;
};

type PlaylistMediaSettingsHeaderActionsProps = FlowScopeProps | SessionProps;

export default function PlaylistMediaSettingsHeaderActions(props: PlaylistMediaSettingsHeaderActionsProps) {
  const t = useTranslations();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { mutateAsync: patchFlow, isPending: flowPending } = usePatchFlowMediaSettingsMutation();
  const { mutateAsync: patchSession, isPending: sessionPending } = usePatchSessionMediaSettingsMutation();

  const isPending = flowPending || sessionPending;
  const labelPrefix = props.mode === 'flowScope' ? 'hosts' : 'speakers';

  const patchField = async (field: 'allowSelfCreate' | 'allowReorder', value: boolean) => {
    if (props.mode === 'flowScope') {
      await patchFlow({
        flowId: props.flowId,
        patch: {
          hosts: {
            scopes: {
              [props.scope]: { [field]: value },
            },
          },
        },
      });
    } else {
      await patchSession({
        sessionId: props.sessionId,
        mediaSettings: {
          speakers: { [field]: value },
        },
      });
    }
  };

  const stored = props.stored;
  const effective = props.effective;
  const canEdit = props.canEdit;
  const disabledTooltip =
    props.mode === 'session' ? props.disabledTooltip : t('media.settings.tooltip.disabled.scopeOverride');
  const scopeSuffix = props.mode === 'flowScope' ? props.scope : 'session';

  return [
    <PlaylistMediaPermissionMenu
      key={`${labelPrefix}-create-${scopeSuffix}`}
      field="allowSelfCreate"
      labelPrefix={labelPrefix}
      allowed={canEdit ? stored.allowSelfCreate : effective.allowSelfCreate}
      canEdit={canEdit}
      isPending={isPending}
      lockedReason={!canEdit ? (disabledTooltip ?? t('media.settings.tooltip.effectiveWhileDisabled')) : undefined}
      testId={`playlist-media-settings-${labelPrefix}-create-${scopeSuffix}`}
      useOutlinedTrigger={!isXs}
      onSelect={(value) => {
        if (canEdit) {
          void patchField('allowSelfCreate', value);
        }
      }}
    />,
    <PlaylistMediaPermissionMenu
      key={`${labelPrefix}-reorder-${scopeSuffix}`}
      field="allowReorder"
      labelPrefix={labelPrefix}
      allowed={canEdit ? stored.allowReorder : effective.allowReorder}
      canEdit={canEdit}
      isPending={isPending}
      lockedReason={!canEdit ? (disabledTooltip ?? t('media.settings.tooltip.effectiveWhileDisabled')) : undefined}
      testId={`playlist-media-settings-${labelPrefix}-reorder-${scopeSuffix}`}
      useOutlinedTrigger={!isXs}
      onSelect={(value) => {
        if (canEdit) {
          void patchField('allowReorder', value);
        }
      }}
    />,
  ];
}
